import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarBlank, X } from "@phosphor-icons/react";
import { api } from "@/lib/api";

export default function JobWonBanner() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const response = await api.get("/notifications", { params: { unread: 1 } });
      setItems(response.data || []);
    } catch {
      /* stay quiet if the request races a logout */
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 15000);
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [load]);

  const unread = items.filter((item) => !item.read);
  const won = unread.find((item) => item.kind === "job_won") || unread[0];
  if (!won && !open) return null;

  const openItem = async (item) => {
    try {
      await api.post(`/notifications/${item.id}/read`);
    } catch {
      /* still navigate */
    }
    setItems((current) => current.filter((row) => row.id !== item.id));
    setOpen(false);
    if (item.href) nav(item.href);
  };

  return (
    <div className="sticky top-0 z-40">
      {won && (
        <div data-testid="job-won-banner" className="bg-zinc-900 text-white px-4 py-3 flex items-start gap-3">
          <button data-testid="notification-bell" onClick={() => setOpen(true)} className="relative mt-0.5 text-orange-400">
            <Bell size={22} weight="fill" />
            <span className="absolute -top-1 -right-1 min-w-4 h-4 px-1 rounded-full bg-orange-600 text-[10px] font-bold flex items-center justify-center">{unread.length}</span>
          </button>
          <button data-testid="job-won-open" onClick={() => openItem(won)} className="flex-1 text-left">
            <p className="text-[10px] uppercase tracking-[0.14em] font-bold text-orange-400">Job won</p>
            <p className="text-sm font-semibold leading-snug">{won.body}</p>
            <p className="mt-1 text-xs font-bold text-white flex items-center gap-1"><CalendarBlank size={14} weight="bold" /> {won.cta || "Schedule this job"}</p>
          </button>
          <button
            data-testid="job-won-dismiss"
            aria-label="Dismiss"
            onClick={async () => {
              try { await api.post(`/notifications/${won.id}/read`); } catch { /* still hide */ }
              setItems((current) => current.filter((row) => row.id !== won.id));
            }}
            className="text-zinc-400"
          >
            <X size={16} />
          </button>
        </div>
      )}
      {open && (
        <div className="bg-white border-b border-zinc-200 px-4 py-3 space-y-2" data-testid="notification-inbox">
          <div className="flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-400">Inbox</p>
            <button onClick={() => setOpen(false)} className="text-xs font-bold text-zinc-500">Close</button>
          </div>
          {unread.length === 0 && <p className="text-sm text-zinc-500">You're caught up.</p>}
          {unread.map((item) => (
            <button key={item.id} data-testid={`notification-${item.id}`} onClick={() => openItem(item)} className="w-full text-left border border-zinc-200 rounded-xl p-3">
              <p className="font-semibold text-zinc-900">{item.title}</p>
              <p className="text-sm text-zinc-600">{item.body}</p>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
